import React from "react";
import type { TranscriptItem } from "../hooks/useTranslator";
import { useUiLang } from "../../../context/UiLangContext";
import { Play, Square, FileText } from "lucide-react";
import { LoadingButton } from "../../../components/LoadingButton";

interface MinutaViewProps {
  meetingTranscript: TranscriptItem[];
  recording: boolean;
  isConnecting?: boolean;
  onOpenSummary: () => void;
  onToggleRecording: () => void;
}

export const MinutaView: React.FC<MinutaViewProps> = ({
  meetingTranscript,
  recording,
  isConnecting,
  onOpenSummary,
  onToggleRecording,
}) => {
  const { uiLang } = useUiLang();
  const isEn = uiLang === "en";
  return (
    <div className="flex-1 flex flex-col p-6 overflow-hidden h-full">
      {/* Centered Start/Stop controls bar at the top */}
      <div className="flex justify-center mb-4">
        <LoadingButton
          onClick={onToggleRecording}
          isLoading={!!isConnecting && !recording}
          className={`py-3.5 px-8 text-sm font-semibold text-white rounded-xl cursor-pointer font-sans  focus:ring-4 ${
            recording
              ? "bg-danger hover:bg-danger/80 focus:ring-danger/35 btn-stop-glow"
              : "bg-accent hover:bg-accent/80 focus:ring-accent/35 btn-start-glow"
          }`}
          style={{
            letterSpacing: "0.02em",
            backgroundColor: recording ? "var(--danger)" : "var(--accent)",
          }}
        >
          {recording ? (
            <Square size={13} fill="currentColor" />
          ) : !isConnecting ? (
            <Play size={15} fill="currentColor" />
          ) : null}
          <span>
            {recording 
              ? (isEn ? "Stop" : "Detener") 
              : isConnecting 
                ? (isEn ? "Connecting..." : "Conectando...") 
                : (isEn ? "Start" : "Iniciar")}
          </span>
        </LoadingButton>
      </div>

      <div className="flex-1 bg-[var(--surface)] border border-[var(--border)] rounded-xl p-5 md:p-8 flex flex-col min-h-0 shadow-lg transition-colors duration-300">

        <div className="flex-1 overflow-y-auto space-y-3.5 pr-2 font-mono scroll-bg scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
          {meetingTranscript.length === 0 ? (
            <p className="text-xs text-[var(--text-dim)] italic text-center py-20">
              {isEn ? "Meeting history will appear here when you start..." : "El historial de la reunión aparecerá aquí al comenzar..."}
            </p>
          ) : (
            meetingTranscript.map((item, index) => (
              <div
                key={index}
                className={`text-xs leading-relaxed p-1.5 rounded transition-all ${
                  item.isMic ? "bg-emerald-500/5 text-emerald-400" : "text-[var(--text-muted)]"
                }`}
              >
                <span className="text-[var(--text-dim)] mr-2.5 font-sans select-none">[{item.timestamp}]</span>
                <span className="select-text">{item.orig}</span>
              </div>
            ))
          )}
        </div>

        {meetingTranscript.length > 0 && !recording && (
          <div className="mt-5 pt-4 border-t border-[var(--border)] flex justify-center">
              <button
                onClick={onOpenSummary}
                className="flex items-center gap-2 px-5 py-2.5 bg-accent hover:bg-accent/80 text-white font-semibold text-xs rounded-lg shadow-lg active:scale-95 transition-all"
              >
                <FileText size={15} />
                {isEn ? "Generate Minutes & Agreements with AI" : "Generar Minuta & Acuerdos con IA"}
              </button>
          </div>
        )}
      </div>
    </div>
  );
};
